// platform/frontend/src/components/plans/EditPlanForm.jsx
import React, { useEffect, useState, useContext, useRef } from "react";
import SignatureField from "../SignatureField";
import { useAuditedPlanOperations } from "../../hooks/useAuditedPlanOperations";

import secureAxios from "../../utils/secureAxios";
import { getApiBaseURL } from "../../utils/api";
import { AuthContext } from "../../contexts/AuthContext";
import "../../assets/css/signature.css";

const EditPlanForm = ({ plan, onCancel, onUpdated }) => {
  const { user, tenant_id } = useContext(AuthContext);
  const { auditPlanUpdate, auditPlanDelete } = useAuditedPlanOperations();

  const apiBase = getApiBaseURL();

  const formatDate = (value) =>
    value ? new Date(value).toISOString().substring(0, 10) : "";

  const [formData, setFormData] = useState({
    ...plan,
    planType: plan.initial_plan
      ? "initial"
      : plan.updated_plan
      ? "updated"
      : "",
    date_of_plan: formatDate(plan.date_of_plan),
    consumer_signature_date: formatDate(plan.consumer_signature_date),
    crp_signature_date: formatDate(plan.crp_signature_date),
    wvdrs_signature_date: formatDate(plan.wvdrs_signature_date),
  });

  // Separate state for signatures - DON'T initialize with plan data
  const [consumerSignature, setConsumerSignature] = useState("");
  const [crpSignature, setCrpSignature] = useState("");
  const [wvdrsSignature, setWvdrsSignature] = useState("");

  const [statuses, setStatuses] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [message, setMessage] = useState("");
  const [showSignatures, setShowSignatures] = useState(false);

  const consumerSigRef = useRef(null);
  const crpSigRef = useRef(null);
  const wvdrsSigRef = useRef(null);

  // Show signature fields after a delay to ensure proper initialization
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSignatures(true);
      // Set signatures after showing the fields
      setTimeout(() => {
        setConsumerSignature(plan.consumer_signature || "");
        setCrpSignature(plan.crp_signature || "");
        setWvdrsSignature(plan.wvdrs_signature || "");
      }, 100);
    }, 300);

    return () => clearTimeout(timer);
  }, [plan.consumer_signature, plan.crp_signature, plan.wvdrs_signature]);

  useEffect(() => {
    secureAxios.get(`${apiBase}/api/status`).then((res) => {
      setStatuses(res.data);
    });

    secureAxios
      .get(`${apiBase}/api/consumers`, {
        params: {
          tenant_id,
          user_id: user?.user_id,
          role: user?.role,
          tenant_type: user.tenant_type,
        },
      })
      .then((res) => setConsumers(res.data))
      .catch((err) => {
        // Log error silently in production
        setMessage("Failed to load consumers. Please refresh the page.");
      });
  }, [tenant_id, user, apiBase]);

  useEffect(() => {
    const consumer = consumers.find(
      (c) => c.consumer_id === formData.consumer_id
    );
    if (!consumer || !consumer.counselor_id) return;

    secureAxios
      .get(`${apiBase}/api/counselors/${consumer.counselor_id}`, {
        params: { tenant_id },
      })
      .then((res) =>
        secureAxios.get(
          `${apiBase}/api/services?tenant_id=${tenant_id}&customer_id=${res.data.customer_id}`
        )
      )
      .then((res) => setAllServices(res.data))
      .catch((err) => {
        // Log error silently in production
        setMessage("Failed to load services. Please refresh the page.");
      });
  }, [formData.consumer_id, consumers, tenant_id, apiBase]);

  useEffect(() => {
    if (!plan.plan_id || !tenant_id) return;

    secureAxios
      .get(
        `${apiBase}/api/plans/${plan.plan_id}/services?tenant_id=${tenant_id}`
      )
      .then((res) => {
        const ids = res.data.map((s) => s.service_id);
        setSelectedServices(ids);
      })
      .catch((err) => {
        // Log error silently in production
        setMessage("Failed to load assigned services.");
      });
  }, [plan.plan_id, tenant_id, apiBase]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceCheckbox = (serviceId) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const updated_by = user?.user_id || "system";

    // Get current signatures - prioritize state, then check refs
    let finalConsumerSig = consumerSignature;
    let finalCrpSig = crpSignature;
    let finalWvdrsSig = wvdrsSignature;

    // If no signature in state but ref has content, use ref
    if (!finalConsumerSig && consumerSigRef.current) {
      finalConsumerSig = consumerSigRef.current.getCurrentSignature() || "";
    }
    if (!finalCrpSig && crpSigRef.current) {
      finalCrpSig = crpSigRef.current.getCurrentSignature() || "";
    }
    if (!finalWvdrsSig && wvdrsSigRef.current) {
      finalWvdrsSig = wvdrsSigRef.current.getCurrentSignature() || "";
    }

    // If still no signature, use the original plan signature
    if (!finalConsumerSig) finalConsumerSig = plan.consumer_signature || "";
    if (!finalCrpSig) finalCrpSig = plan.crp_signature || "";
    if (!finalWvdrsSig) finalWvdrsSig = plan.wvdrs_signature || "";

    const payload = {
      ...formData,
      initial_plan: formData.planType === "initial",
      updated_plan: formData.planType === "updated",
      tenant_id,
      updated_by,
      consumer_signature: finalConsumerSig,
      crp_signature: finalCrpSig,
      wvdrs_signature: finalWvdrsSig,
    };

    try {
      await secureAxios.put(`${apiBase}/api/plans/${plan.plan_id}`, payload);

      await auditPlanUpdate(plan.plan_id, plan.plan_name, {
        old: { status: plan.status_id },
        new: { status: formData.status_id },
      });

      await secureAxios.post(`${apiBase}/api/plans/assign-services`, {
        plan_id: plan.plan_id,
        tenant_id,
        service_ids: selectedServices,
      });

      setMessage("Plan updated!");
      setTimeout(onUpdated, 1000);
    } catch (err) {
      // Log error silently in production
      setMessage("Failed to update plan. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        "WARNING: Deleting this plan will permanently remove Protected Health Information. This action will be logged. Are you sure you want to delete this plan?"
      )
    ) {
      try {
        await secureAxios.delete(
          `${apiBase}/api/plans/${plan.plan_id}?tenant_id=${tenant_id}`
        );
        await auditPlanDelete(plan);
        setMessage("Plan deleted!");
        setTimeout(onUpdated, 1000);
      } catch (err) {
        // Log error silently in production
        setMessage("Failed to delete plan.");
      }
    }
  };

  return (
    <div>
      <h2>Edit Plan</h2>

      {/* HIPAA Compliance Notice */}
      <div className="alert alert-info mb-4">
        <h6 className="alert-heading">
          <i className="bi bi-shield-lock-fill me-2"></i>
          Protected Health Information (PHI) Notice
        </h6>
        <p className="mb-2">
          You are editing Protected Health Information. Please ensure:
        </p>
        <ul className="mb-0">
          <li>All changes are medically necessary and authorized</li>
          <li>You maintain the accuracy and integrity of the information</li>
          <li>All modifications are being tracked in an audit log</li>
          <li>You do not share this information outside authorized channels</li>
        </ul>
      </div>

      {message && <p className="fw-bold">{message}</p>}
      <form onSubmit={handleSubmit} className="row g-3">
        <div className="col-md-12">
          <label className="form-label">Plan Name</label>
          <input
            className="form-control"
            name="plan_name"
            value={formData.plan_name || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Consumer</label>
          <select
            name="consumer_id"
            value={formData.consumer_id}
            onChange={handleChange}
            className="form-select"
            required
          >
            <option value="">Select Consumer</option>
            {consumers.map((c) => (
              <option key={c.consumer_id} value={c.consumer_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label">Date of Plan:</label>
          <input
            type="date"
            name="date_of_plan"
            value={formData.date_of_plan}
            onChange={handleChange}
            className="form-control"
          />
        </div>

        <div className="col-md-12">
          <label className="form-label">Services</label>
          <div className="row">
            {allServices.map((service) => (
              <div className="col-md-4" key={service.service_id}>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`s-${service.service_id}`}
                    checked={selectedServices.includes(service.service_id)}
                    onChange={() => handleServiceCheckbox(service.service_id)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`s-${service.service_id}`}
                  >
                    {service.service_name}
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Plan Type */}
        <div className="col-md-12 d-flex gap-1 align-items-center">
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="planType"
              value="initial"
              checked={formData.planType === "initial"}
              onChange={handleChange}
            />
            <label className="form-check-label">Initial Plan</label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="planType"
              value="updated"
              checked={formData.planType === "updated"}
              onChange={handleChange}
            />
            <label className="form-check-label">Updated Plan</label>
          </div>
        </div>

        <div className="col-md-12">
          <label className="form-label">
            Individuals involved in the development of plan: Name
            (Consumer/Agency/Title/Family Member/Other)
          </label>
          <textarea
            name="participants"
            value={formData.participants}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="col-md-12">
          <label className="form-label">Strengths and resources:</label>
          <textarea
            name="strengths_and_resources"
            value={formData.strengths_and_resources}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="col-md-12">
          <label className="form-label">Issues/needs identified:</label>
          <textarea
            name="issues_identified"
            value={formData.issues_identified}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="col-md-12">
          <label className="form-label">
            Cultural considerations, resources and needs:
          </label>
          <textarea
            name="cultural"
            value={formData.cultural}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="col-md-12">
          <label className="form-label">
            Coordination of Services - (identify other services consumer is
            receiving and explain how they are being coordinated):
          </label>
          <textarea
            name="coordination"
            value={formData.coordination}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        {/* Digital Signature Section with HIPAA Notice */}
        <div className="col-12">
          <div className="alert alert-warning">
            <h6 className="alert-heading">
              <i className="bi bi-pen me-2"></i>
              Digital Signature Update Notice
            </h6>
            <p className="mb-0">
              Updating signatures creates a new legal authorization. All parties
              must re-sign if changes are made to the plan. Previous signatures
              will be replaced.
            </p>
          </div>
        </div>

        <div className="col-md-12">
          <label className="form-label">Consumer Signature</label>
          <div
            style={{
              border: "1px solid #ccc",
              padding: "10px",
              minHeight: "180px",
            }}
          >
            {showSignatures ? (
              <SignatureField
                ref={consumerSigRef}
                value={consumerSignature}
                onChange={setConsumerSignature}
                penColor="blue"
                label=""
              />
            ) : (
              <div
                style={{
                  height: "150px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span className="text-muted">Loading signature...</span>
              </div>
            )}
          </div>
          <div className="col-md-6 mt-2">
            <label className="form-label">Consumer Signature Date</label>
            <input
              type="date"
              className="form-control"
              name="consumer_signature_date"
              value={formData.consumer_signature_date}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="col-md-12">
          <label className="form-label">CRP Signature</label>
          <div
            style={{
              border: "1px solid #ccc",
              padding: "10px",
              minHeight: "180px",
            }}
          >
            {showSignatures ? (
              <SignatureField
                ref={crpSigRef}
                value={crpSignature}
                onChange={setCrpSignature}
                penColor="blue"
                label=""
              />
            ) : (
              <div
                style={{
                  height: "150px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span className="text-muted">Loading signature...</span>
              </div>
            )}
          </div>
          <div className="col-md-6 mt-2">
            <label className="form-label">CRP Signature Date</label>
            <input
              type="date"
              className="form-control"
              name="crp_signature_date"
              value={formData.crp_signature_date}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="col-md-12">
          <label className="form-label">WVDRS Signature</label>
          <div
            style={{
              border: "1px solid #ccc",
              padding: "10px",
              minHeight: "180px",
            }}
          >
            {showSignatures ? (
              <SignatureField
                ref={wvdrsSigRef}
                value={wvdrsSignature}
                onChange={setWvdrsSignature}
                penColor="blue"
                label=""
              />
            ) : (
              <div
                style={{
                  height: "150px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span className="text-muted">Loading signature...</span>
              </div>
            )}
          </div>
          <div className="col-md-6 mt-2">
            <label className="form-label">WVDRS Signature Date</label>
            <input
              type="date"
              className="form-control"
              name="wvdrs_signature_date"
              value={formData.wvdrs_signature_date}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {[
          "goals_and_objectives",
          "timeframe",
          "frequency",
          "responsible_person",
        ].map((key) => (
          <div className="col-md-6" key={key}>
            <label className="form-label">
              {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
            </label>
            <textarea
              className="form-control"
              name={key}
              value={formData[key]}
              onChange={handleChange}
            />
          </div>
        ))}

        {/* Delete confirmation with HIPAA warning */}
        <div className="col-12 d-flex justify-content-between">
          <div>
            <button
              className="btn btn-danger me-2"
              type="button"
              onClick={handleDelete}
            >
              Delete Plan
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
          <button className="btn btn-primary" type="submit">
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditPlanForm;